import { useEffect, useState } from 'react';
import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { useAsync } from 'react-use';
export type EntityInfo = { displayName: string; picture?: string };

/**
 * Converts a meltwater email address to a Backstage User entity ref.
 * Replicates the normalizeEntityName transform used by the MS Graph catalog module:
 * lowercase, replace every char not in [a-zA-Z0-9_\-.] with `_`, collapse `__`→`_`,
 * trim trailing `_`. In practice only `@` is replaced for normal meltwater emails.
 * e.g. nick.telford@meltwater.com → user:default/nick.telford_meltwater.com
 */
export const emailToUserRef = (email: string): string => {
  const name = email
    .toLowerCase()
    .replace(/[^a-zA-Z0-9_\-.]/g, '_')
    .replace(/__+/g, '_')
    .replace(/_+$/, '');
  return `user:default/${name}`;
};

/**
 * Resolves a list of email addresses to a userMap keyed by entity ref
 * (user:default/<normalized-email>). Mirrors the resolution pattern in
 * EntityGroupOverviewCard. Falls back to the email local-part as displayName
 * for unresolved emails so chips always show a clean label.
 *
 * Gracefully degrades on catalog errors — populates local-part-only entries
 * and never throws.
 */
export const useUsersByEmail = (
  emails: string[],
): { userMap: Map<string, EntityInfo>; loading: boolean } => {
  const catalogApi = useApi(catalogApiRef);

  // Dedup + lowercase to match OpsGenie casing behaviour
  const normalised = [...new Set(emails.map(e => e.toLowerCase()))].filter(
    Boolean,
  );
  const depKey = normalised.sort().join('|');

  const { value: userMap = new Map<string, EntityInfo>(), loading } = useAsync(
    async () => {
      const map = new Map<string, EntityInfo>();
      if (!normalised.length) return map;

      const refs = normalised.map(emailToUserRef);

      // Seed the map with local-part fallbacks immediately so chips render
      // a clean label even before the catalog resolves.
      for (let i = 0; i < normalised.length; i++) {
        map.set(refs[i], {
          displayName: normalised[i].replace(/@.*$/, ''),
        });
      }

      try {
        const { items } = await catalogApi.getEntitiesByRefs({
          entityRefs: refs,
          fields: ['kind', 'metadata', 'spec'],
        });
        for (let i = 0; i < refs.length; i++) {
          const item = items[i];
          if (!item) continue;
          const itemProfile = (item.spec as Record<string, unknown> | undefined)
            ?.profile as Record<string, string> | undefined;
          map.set(refs[i], {
            displayName:
              itemProfile?.displayName ??
              item.metadata.title ??
              item.metadata.name,
            picture: itemProfile?.picture,
          });
        }
      } catch {
        // Catalog unavailable — the local-part fallbacks seeded above are fine
      }

      return map;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [depKey],
  );

  return { userMap, loading };
};

/**
 * Resolves a list of entity refs (user:default/x or group:default/y) to a
 * userMap keyed by entity ref. Seeds fallback display names immediately so
 * chips render before the catalog responds.
 */
const CATALOG_RETRY_DELAY_MS = 30_000;

export const useEntityRefInfoMap = (
  refs: string[],
): Map<string, EntityInfo> => {
  const catalogApi = useApi(catalogApiRef);
  const unique = [...new Set(refs)].filter(Boolean);
  const depKey = [...unique].sort().join('|');
  const [retryKey, setRetryKey] = useState(0);

  const { value, error } = useAsync(async () => {
    const map = new Map<string, EntityInfo>();
    if (!unique.length) return map;

    // Seed fallback display names immediately so chips render before the catalog responds
    for (const ref of unique) {
      map.set(ref, { displayName: ref.split('/').pop() ?? ref });
    }

    const { items } = await catalogApi.getEntitiesByRefs({
      entityRefs: unique,
      fields: ['kind', 'metadata', 'spec'],
    });
    for (let i = 0; i < unique.length; i++) {
      const entity = items[i];
      if (!entity) continue;
      const profile = (entity.spec as Record<string, unknown> | undefined)
        ?.profile as Record<string, string> | undefined;
      map.set(unique[i], {
        displayName:
          profile?.displayName ?? entity.metadata.title ?? entity.metadata.name,
        picture: profile?.picture,
      });
    }

    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [depKey, retryKey]);

  // Retry after a delay when the catalog is temporarily unavailable so stub
  // display names don't persist indefinitely after the catalog recovers.
  useEffect(() => {
    if (!error) return;
    const id = setTimeout(
      () => setRetryKey(k => k + 1),
      CATALOG_RETRY_DELAY_MS,
    );
    return () => clearTimeout(id);
  }, [error]);

  return value ?? new Map();
};
